/**
 * `/schedules/:id` — one schedule and its run history.
 *
 * The header offers the same acts as the schedule's row, from
 * `../entities/scheduleActions`. A run by hand reloads the history, so its
 * outcome appears at the top of the table.
 *
 * The server keeps the newest 100 runs of a schedule; the rest are in the log,
 * found by a run's correlation id.
 */
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import HistoryIcon from '@mui/icons-material/History';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ScheduleRunDto } from '@forsys/shared';
import { getSchedule, listScheduleJobs, listScheduleRuns } from '../../api/schedules';
import { AsyncContent } from '../../components/AsyncContent';
import { Details } from '../../components/Details';
import { EntityDetail } from '../../components/EntityDetail';
import { EntityTable } from '../../components/EntityTable';
import { Panel } from '../../components/Panel';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useListQuery } from '../../hooks/useListQuery';
import { useScheduleActions } from '../entities/scheduleActions';
import { RunStatusChip } from './RunStatusChip';

/** Seconds a run took, or null while it runs. */
function duration(run: ScheduleRunDto): number | null {
  if (run.finishedAt === null) return null;
  return Math.max(0, (Date.parse(run.finishedAt) - Date.parse(run.startedAt)) / 1000);
}

export default function ScheduleDetailPage() {
  const { t } = useTranslation('schedules');
  const { id } = useParams();
  const scheduleId = Number(id);
  const navigate = useNavigate();

  const schedule = useAsync(() => getSchedule(scheduleId), [scheduleId]);
  const jobs = useAsync(() => listScheduleJobs(), []);
  const list = useListQuery({ defaultSort: 'startedAt', defaultOrder: 'desc' });
  const runs = useAsync(() => listScheduleRuns(scheduleId, list.query), [scheduleId, list.key]);

  const actions = useScheduleActions({
    jobs: jobs.data?.jobs ?? [],
    onChanged: () => {
      schedule.reload();
      runs.reload();
    },
    onDeleted: () => navigate('/schedules'),
  });

  return (
    <AsyncContent state={schedule}>
      {({ schedule: record }) => (
        <EntityDetail
          backTo="/schedules"
          backLabel={t('detail.back')}
          title={record.name}
          badges={!record.isEnabled && <Chip size="small" label={t('disabled')} />}
          description={jobs.data?.jobs.find((job) => job.key === record.job)?.description}
          entity={actions.forRow(record)}
          error={actions.error}
        >
          <Stack spacing={3}>
            <Panel title={t('detail.settings')}>
              <Details
                rows={[
                  [t('column.job'), <Box component="code" key="job">{record.job}</Box>],
                  [t('detail.cron'), <Box component="code" key="cron">{record.cron}</Box>],
                  [t('detail.timeZone'), record.timeZone],
                  [t('column.nextRun'), record.nextRunAt ? <Timestamp value={record.nextRunAt} absolute /> : t('disabled')],
                  [t('column.lastRun'), record.lastRunAt && <Timestamp value={record.lastRunAt} absolute />],
                  [
                    t('detail.input'),
                    record.input !== null && (
                      <Box
                        component="pre"
                        sx={{ m: 0, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}
                      >
                        {JSON.stringify(record.input, null, 2)}
                      </Box>
                    ),
                  ],
                ]}
              />
            </Panel>

            <Panel title={t('detail.runs')} icon={<HistoryIcon fontSize="small" />}>
              <AsyncContent state={runs}>
                {({ runs: rows, page }) => (
                  <EntityTable
                    rows={rows}
                    rowKey={(run) => run.id}
                    list={list}
                    page={page}
                    size="small"
                    empty={{
                      icon: <HistoryIcon fontSize="inherit" />,
                      title: t('detail.noRunsTitle'),
                      description: t('detail.noRunsDescription'),
                    }}
                    columns={[
                      {
                        field: 'startedAt',
                        label: t('detail.column.started'),
                        sortable: true,
                        width: 170,
                        render: (run) => <Timestamp value={run.startedAt} />,
                      },
                      {
                        field: 'status',
                        label: t('detail.column.status'),
                        width: 120,
                        render: (run) => <RunStatusChip status={run.status} />,
                      },
                      {
                        field: 'trigger',
                        label: t('detail.column.trigger'),
                        width: 110,
                        render: (run) => (
                          <Typography variant="caption" color="text.secondary">
                            {t(`trigger.${run.trigger}`)}
                          </Typography>
                        ),
                      },
                      {
                        field: 'duration',
                        label: t('detail.column.duration'),
                        width: 90,
                        render: (run) => {
                          const seconds = duration(run);
                          return (
                            <Typography variant="caption" color="text.secondary">
                              {seconds === null ? '…' : t('detail.seconds', { seconds: seconds.toFixed(1) })}
                            </Typography>
                          );
                        },
                      },
                      {
                        field: 'outcome',
                        label: t('detail.column.outcome'),
                        render: (run) =>
                          run.error ? (
                            <Typography variant="caption" color="error.main" sx={{ overflowWrap: 'anywhere' }}>
                              {run.error}
                            </Typography>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}
                            >
                              {run.result ? JSON.stringify(run.result) : ''}
                            </Typography>
                          ),
                      },
                    ]}
                  />
                )}
              </AsyncContent>
            </Panel>
          </Stack>
        </EntityDetail>
      )}
    </AsyncContent>
  );
}
